import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Helmet } from 'react-helmet'
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom'
import { DateTime } from 'luxon'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import LinearProgress from '@material-ui/core/LinearProgress'
import Container from '@material-ui/core/Container'
import ArrowBackOutlinedIcon from 'components/material-symbol/icons/ArrowBack'
import CheckIcon from 'components/material-symbol/icons/Check'
import SaveIcon from 'components/material-symbol/icons/Save'
import Error from 'components/error'
import { amplitudeEvent } from 'lib/amplitude'
import { useAuth } from 'lib/auth'
import { useDebounce } from 'lib/hooks'
import { useSnackbar, useSnackbarError, useSnackbarSuccess } from 'lib/snackbar'
import { useStyles as useFlowStyles } from 'pages/pga-coach/bookings/events/edit/edit-event-form/styles'
import FormSectionContainer from 'pages/pga-coach/bookings/events/edit/edit-event-form/form-section-container'
import EditEventPagination from 'pages/pga-coach/bookings/events/edit/edit-event-form/edit-event-pagination'
import CreateProfileSectionNav from 'pages/pga-coach/profile/new/create-profile-form/create-profile-section-nav'
import {
  useCoachCampaign,
  useCoachCampaignAudience,
  useCoachCampaignOptions,
  useCreateCoachCampaign,
  useSendCoachCampaign,
  useUpdateCoachCampaign,
} from '../api-hooks'
import {
  CAMPAIGNS_PATH,
  EMPTY_CAMPAIGN,
  LOCAL_INPUT_FORMAT,
  MAX_SCHEDULE_DAYS,
  SECTIONS,
  STEP_PARAM,
} from '../constants'
import {
  filtersToInput,
  isHandPicked,
  pluralize,
  toCampaignInput,
  toCampaignState,
} from '../format'
import AudienceStep from './audience-step'
import ContentStep from './content-step'
import ReviewStep from './review-step'
import { firstInvalidStep, isBodyTooLong, isStepValid } from './validation'
import useStyles from '../styles'

// The audience query's page limit, so a draft's picks all show as chips.
const MAX_PICKED_PREVIEW = 100

const toOptions = data => {
  const coach = data?.currentUser?.attributes
  const lessonTypes = data?.lessonTypes || []
  const upcomingGroupEvents = data?.currentUser?.upcomingGroupEvents || []

  return {
    audience: {
      lessonTypes: lessonTypes.map(({ id, title }) => ({ id, label: title })),
      groupEvents: (data?.currentUser?.pastGroupEvents || []).map(
        ({ id, title, startDateTime }) => ({
          id,
          label: `${title} · ${DateTime.fromISO(startDateTime).toFormat(
            'LLL d, yyyy',
          )}`,
        }),
      ),
      programs: (coach?.jrlPrograms || []).map(({ id, name, year }) => ({
        id,
        label: year ? `${name} (${year})` : name,
      })),
      facilities: (coach?.facilities || []).map(({ id, name }) => ({
        id,
        label: name,
      })),
    },
    offerings: {
      lessonTypes: lessonTypes
        .filter(lessonType => lessonType.status === 'ACTIVE')
        .map(({ id, title }) => ({ id, title })),
      groupEvents: upcomingGroupEvents.map(({ id, title, startDateTime }) => ({
        id,
        title,
        startDateTime,
      })),
    },
  }
}

// The step named by ?step=, pulled back to the first step the draft can't
// skip: no name starts at Audience, no message (or one that is too long) at
// Message.
const getStartStep = (campaign, search) => {
  const requested = SECTIONS.findIndex(
    section =>
      section.id ===
      `campaign-section-${new URLSearchParams(search).get(STEP_PARAM)}`,
  )
  if (requested <= 0) return 0
  const blockedAt = firstInvalidStep(campaign)
  return blockedAt === -1 ? requested : Math.min(requested, blockedAt)
}

const defaultScheduledFor = () =>
  DateTime.local()
    .plus({ days: 1 })
    .set({ hour: 9, minute: 0 })
    .toFormat(LOCAL_INPUT_FORMAT)

export function CampaignWizardForm({ initialCampaign, hasPaidTier }) {
  const classes = useStyles()
  const flowClasses = useFlowStyles()
  const history = useHistory()
  const { search } = useLocation()
  const { user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const showError = useSnackbarError(enqueueSnackbar)
  const showSuccess = useSnackbarSuccess(enqueueSnackbar)

  const [campaign, setCampaign] = useState(initialCampaign)
  const [startStep] = useState(() => getStartStep(initialCampaign, search))
  const [step, setStep] = useState(startStep)
  const [furthestStep, setFurthestStep] = useState(startStep)
  const checkAudienceOnStart = useRef(startStep > 0)
  const [audienceMode, setAudienceMode] = useState(
    isHandPicked(initialCampaign.audienceFilters) ? 'pick' : 'filters',
  )
  const [pickedStudents, setPickedStudents] = useState([])
  const [nameError, setNameError] = useState(false)
  const [bodyError, setBodyError] = useState(false)
  const [delivery, setDelivery] = useState('now')
  const [scheduledFor, setScheduledFor] = useState(defaultScheduledFor)
  const [scheduleError, setScheduleError] = useState(null)
  const [busy, setBusy] = useState(false)

  const [createCampaign] = useCreateCoachCampaign()
  const [updateCampaign] = useUpdateCoachCampaign()
  const [sendCampaign] = useSendCoachCampaign()

  const { data: optionsData } = useCoachCampaignOptions({ skip: !hasPaidTier })
  const options = useMemo(() => toOptions(optionsData), [optionsData])

  const debouncedFilters = useDebounce(campaign.audienceFilters, 400)
  const waitingForPicks =
    audienceMode === 'pick' && debouncedFilters.studentIds.length === 0
  const {
    data: audienceData,
    loading: audienceLoading,
  } = useCoachCampaignAudience(filtersToInput(debouncedFilters), {
    skip: !hasPaidTier || waitingForPicks,
    limit: audienceMode === 'pick' ? MAX_PICKED_PREVIEW : undefined,
  })
  const audience = waitingForPicks
    ? { count: 0, maxRecipients: 0, students: [] }
    : audienceData?.coachCampaignAudience
  const audienceCount = audience?.count ?? 0
  const maxRecipients = audience?.maxRecipients
  const overLimit = Boolean(maxRecipients) && audienceCount > maxRecipients
  const audienceReady =
    debouncedFilters === campaign.audienceFilters &&
    !audienceLoading &&
    audienceCount > 0 &&
    !overLimit

  // A draft opened past Audience goes back there when it has nobody to send to.
  useEffect(() => {
    if (!checkAudienceOnStart.current) return
    if (!waitingForPicks && !audienceData) return
    checkAudienceOnStart.current = false
    if (audienceCount === 0 || overLimit) {
      setStep(0)
      setFurthestStep(0)
      showError(
        overLimit
          ? `Campaigns can go to up to ${maxRecipients.toLocaleString()} students. Narrow your filters to continue.`
          : 'No students match this audience. Change your filters or picks to continue.',
      )
    }
  }, [
    waitingForPicks,
    audienceData,
    audienceCount,
    maxRecipients,
    overLimit,
    showError,
  ])

  const pickedIds = campaign.audienceFilters.studentIds
  useEffect(() => {
    const students = audienceData?.coachCampaignAudience?.students || []
    setPickedStudents(current => {
      const shown = new Set(current.map(student => student.id))
      const found = students.filter(
        student => pickedIds.includes(student.id) && !shown.has(student.id),
      )
      return found.length > 0 ? [...current, ...found] : current
    })
  }, [audienceData, pickedIds])

  // Picks not shown as chips yet (still loading, or past the preview limit)
  // are kept, so editing the chips never drops them.
  const pickStudents = students => {
    const shown = new Set(pickedStudents.map(student => student.id))
    const unshown = pickedIds.filter(id => !shown.has(id))
    setPickedStudents(students)
    change({
      audienceFilters: {
        ...campaign.audienceFilters,
        studentIds: [...unshown, ...students.map(student => student.id)],
      },
    })
  }

  const change = patch => {
    setCampaign(current => ({ ...current, ...patch }))
    if (patch.name?.trim()) setNameError(false)
    if (patch.body !== undefined && isStepValid({ body: patch.body }, 1)) {
      setBodyError(false)
    }
  }

  const changeAudienceMode = mode => {
    setAudienceMode(mode)
    if (mode === 'filters') setPickedStudents([])
    change({
      audienceFilters: {
        ...campaign.audienceFilters,
        excludedStudentIds: [],
        ...(mode === 'filters' ? { studentIds: [] } : {}),
      },
    })
  }

  const persist = async () => {
    const input = toCampaignInput(campaign)
    try {
      const { data } = campaign.id
        ? await updateCampaign({ variables: { id: campaign.id, input } })
        : await createCampaign({ variables: { input } })
      const response = data?.updateCoachCampaign || data?.createCoachCampaign
      if (!response?.success) {
        showError(response?.message)
        return null
      }
      setCampaign(current => ({ ...current, id: response.campaign.id }))
      return response.campaign
    } catch (e) {
      showError()
      return null
    }
  }

  const validateStep = index => {
    if (isStepValid(campaign, index)) return true
    if (index === 0) setNameError(true)
    else setBodyError(true)
    return false
  }

  const goToStep = async target => {
    if (busy) return
    let destination = target
    for (let index = step; index < target; index += 1) {
      if (!validateStep(index) || (index === 0 && !audienceReady)) {
        destination = index
        break
      }
    }
    if (destination === step) return
    setBusy(true)
    const saved = await persist()
    setBusy(false)
    if (saved) {
      setStep(destination)
      setFurthestStep(current => Math.max(current, destination))
      window.scrollTo(0, 0)
    }
  }

  const goNext = () => goToStep(step + 1)

  const goPrevious = () => {
    if (busy) return
    setStep(step - 1)
    window.scrollTo(0, 0)
  }

  const goToSection = sectionId => {
    const index = SECTIONS.findIndex(section => section.id === sectionId)
    if (busy || index === step) return
    if (index < step) {
      setStep(index)
      window.scrollTo(0, 0)
      return
    }
    goToStep(index)
  }

  const saveDraft = async () => {
    if (!validateStep(0)) {
      setStep(0)
      return
    }
    if (isBodyTooLong(campaign.body)) {
      setStep(1)
      setBodyError(true)
      return
    }
    setBusy(true)
    const saved = await persist()
    setBusy(false)
    if (saved) {
      showSuccess('Draft saved')
      history.push(CAMPAIGNS_PATH)
    }
  }

  const minScheduledFor = DateTime.local()
    .plus({ minutes: 5 })
    .toFormat(LOCAL_INPUT_FORMAT)
  const maxScheduledFor = DateTime.local()
    .plus({ days: MAX_SCHEDULE_DAYS })
    .toFormat(LOCAL_INPUT_FORMAT)

  const send = async () => {
    for (let index = 0; index < SECTIONS.length - 1; index += 1) {
      if (!validateStep(index)) {
        setStep(index)
        return
      }
    }

    let scheduledForIso = null
    if (delivery === 'later') {
      const scheduledAt = DateTime.fromFormat(scheduledFor, LOCAL_INPUT_FORMAT)
      if (!scheduledAt.isValid || scheduledAt <= DateTime.local()) {
        setScheduleError('Choose a time in the future')
        return
      }
      if (scheduledAt > DateTime.local().plus({ days: MAX_SCHEDULE_DAYS })) {
        setScheduleError(`Choose a time within ${MAX_SCHEDULE_DAYS} days`)
        return
      }
      setScheduleError(null)
      scheduledForIso = scheduledAt.toUTC().toISO()
    }

    setBusy(true)
    const saved = await persist()
    if (!saved) {
      setBusy(false)
      return
    }
    try {
      const { data } = await sendCampaign({
        variables: { id: saved.id, scheduledFor: scheduledForIso },
      })
      const response = data?.sendCoachCampaign
      if (!response?.success) {
        showError(response?.message)
        setBusy(false)
        return
      }
      if (!user?.isAssumed) {
        amplitudeEvent('coach-campaign-sent', {
          campaign_id: saved.id,
          scheduled: Boolean(scheduledForIso),
          recipient_count: response.campaign.recipientCount,
          offering_count: campaign.offerings.length,
          media_count: campaign.media.length,
        })
      }
      showSuccess(
        scheduledForIso
          ? 'Campaign scheduled'
          : `Sent to ${pluralize(response.campaign.recipientCount, 'student')}`,
      )
      history.push(`${CAMPAIGNS_PATH}/${saved.id}`)
    } catch (e) {
      showError()
      setBusy(false)
    }
  }

  const lastStep = step === SECTIONS.length - 1
  const currentSection = SECTIONS[step]
  const completedSectionIds = new Set(
    SECTIONS.slice(0, furthestStep).map(section => section.id),
  )

  const renderSection = () => {
    switch (currentSection.id) {
      case 'campaign-section-audience':
        return (
          <AudienceStep
            campaign={campaign}
            options={options.audience}
            audience={audience}
            audienceLoading={audienceLoading}
            mode={audienceMode}
            pickedStudents={pickedStudents}
            nameError={nameError}
            skipSearch={!hasPaidTier}
            onChange={change}
            onModeChange={changeAudienceMode}
            onPickStudents={pickStudents}
          />
        )
      case 'campaign-section-message':
        return (
          <ContentStep
            campaign={campaign}
            offeringOptions={options.offerings}
            bodyError={bodyError}
            onChange={change}
          />
        )
      case 'campaign-section-review':
        return (
          <ReviewStep
            campaign={campaign}
            audienceCount={audienceCount}
            delivery={delivery}
            scheduledFor={scheduledFor}
            minScheduledFor={minScheduledFor}
            maxScheduledFor={maxScheduledFor}
            scheduleError={scheduleError}
            onDeliveryChange={setDelivery}
            onScheduledForChange={value => {
              setScheduledFor(value)
              setScheduleError(null)
            }}
          />
        )
      default:
        return null
    }
  }

  return (
    <>
      <Helmet>
        <script
          src="https://widget.cloudinary.com/v2.0/global/all.js"
          type="text/javascript"
        />
      </Helmet>
      <Box className={flowClasses.actionBar}>
        <Button
          type="button"
          className={flowClasses.cancelAction}
          startIcon={<ArrowBackOutlinedIcon fontSize="small" />}
          onClick={() => history.push(CAMPAIGNS_PATH)}
        >
          Cancel
        </Button>
        <Box className={classes.actionBarActions}>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            className={flowClasses.saveAction}
            disabled={busy}
            startIcon={<SaveIcon fontSize="small" />}
            onClick={saveDraft}
          >
            Save Draft
          </Button>
          <Button
            type="button"
            variant="contained"
            color="primary"
            className={flowClasses.saveAction}
            disabled={busy || !lastStep || !audienceReady}
            disableElevation
            startIcon={<CheckIcon fontSize="small" />}
            onClick={send}
          >
            {delivery === 'later'
              ? 'Schedule Campaign'
              : `Send to ${pluralize(audienceCount, 'Student')}`}
          </Button>
        </Box>
      </Box>
      {busy ? (
        <LinearProgress color="primary" className={flowClasses.loadingStyles} />
      ) : null}
      <Container maxWidth="lg">
        <Box pt={4} pb={3}>
          <Box className={flowClasses.layoutGrid}>
            <Box className={flowClasses.navColumn}>
              <CreateProfileSectionNav
                sections={SECTIONS}
                activeSection={currentSection.id}
                completedSectionIds={completedSectionIds}
                onSectionClick={goToSection}
                ariaLabel="Campaign sections"
                showCompletedCheck={false}
              />
            </Box>

            <Box className={flowClasses.formColumn}>
              <FormSectionContainer
                id={currentSection.id}
                title={currentSection.pageTitle}
              >
                <Box className={classes.stepContent}>{renderSection()}</Box>
              </FormSectionContainer>
              <EditEventPagination
                isFirstStep={step === 0}
                isLastStep={lastStep}
                onPrevious={goPrevious}
                onNext={goNext}
                isNextDisabled={busy || (step === 0 && !audienceReady)}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </>
  )
}

CampaignWizardForm.propTypes = {
  initialCampaign: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    audienceFilters: PropTypes.object.isRequired,
    media: PropTypes.array.isRequired,
    offerings: PropTypes.array.isRequired,
  }).isRequired,
  hasPaidTier: PropTypes.bool,
}

const CampaignWizard = () => {
  const { id } = useParams()
  const { user } = useAuth()
  const hasPaidTier = Boolean(user?.coach?.hasPaidTierSubscription)
  const { data, loading, error } = useCoachCampaign(id, {
    skip: !hasPaidTier,
  })

  if (loading) return <LinearProgress color="primary" />
  if (error) return <Error error={error} />

  const campaign = data?.coachCampaign
  if (id && hasPaidTier && !campaign) return <Error statusCode={404} />
  if (id && campaign && !campaign.editable) {
    return <Redirect to={`${CAMPAIGNS_PATH}/${id}`} />
  }

  return (
    <CampaignWizardForm
      key={id || 'new'}
      initialCampaign={campaign ? toCampaignState(campaign) : EMPTY_CAMPAIGN}
      hasPaidTier={hasPaidTier}
    />
  )
}

export default CampaignWizard
